import { useId } from 'react';
import { RadioButton, Tooltip } from '@hopskipdrive/react-components';
import { colorTokens, variables } from '@rideiq/shared/components/variables';
import styled from 'styled-components';

import { TOOLTIP_HOVER_DELAY_MS } from '../../../constants';

export type RadioOptionCardProps = {
  label: string;
  value?: string;
  subtitle: string;
  checked: boolean;
  onSelect: () => void;
  tooltip?: string;
  error?: boolean;
  errorMessage?: string;
};

export const RadioOptionCard = ({
  label,
  value = label,
  subtitle,
  checked,
  onSelect,
  tooltip,
  error = false,
  errorMessage,
}: RadioOptionCardProps) => {
  const helperId = useId();
  const showHelperText = error && Boolean(errorMessage);
  const card = (
    <Card $error={error} data-testid={`radio-option-card-${label}`}>
      <RadioButton
        checked={checked}
        value={value}
        onChange={onSelect}
        inputProps={{ 'aria-label': label, 'aria-describedby': showHelperText ? helperId : undefined }}
      />
      <TextBlock>
        <Label>{label}</Label>
        <Subtitle>{subtitle}</Subtitle>
      </TextBlock>
    </Card>
  );

  const cardWithTooltip = tooltip ? (
    <Tooltip
      title={tooltip}
      enterDelay={TOOLTIP_HOVER_DELAY_MS}
      enterNextDelay={TOOLTIP_HOVER_DELAY_MS}
      enterTouchDelay={0}
      leaveTouchDelay={3000}
    >
      {card}
    </Tooltip>
  ) : (
    card
  );

  return (
    <Wrapper>
      {cardWithTooltip}
      {showHelperText && (
        <HelperText id={helperId} data-testid="option-card-helper-text">
          {errorMessage}
        </HelperText>
      )}
    </Wrapper>
  );
};

const mqDesktop = `@media (max-width: ${variables.breakpoints.desktop - 1}px)`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: calc(50% - 6px);
  ${mqDesktop} {
    width: 100%;
  }
`;

const Card = styled.label<{ $error: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  padding: 16px;
  border: 1px solid ${({ $error }) => ($error ? colorTokens.error.main : colorTokens.border.default)};
  border-radius: 10px;
  background-color: ${({ $error }) => ($error ? colorTokens.error.surface : colorTokens.background.default)};
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  &:focus-within {
    outline: 2px solid ${colorTokens.primary.main};
    outline-offset: 2px;
  }
`;

const TextBlock = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Label = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colorTokens.text.dark};
`;

const Subtitle = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colorTokens.text.medium};
`;

const HelperText = styled.span`
  padding: 0 14px;
  font-size: 12px;
  color: ${colorTokens.error.dark};
`;
